import { useState } from 'react';
import { useNavigate } from 'react-router';
import { PhonePairing } from '../components/PhonePairing';
import { formatDe, parseGermanDate, todayIn } from '@frisk/shared';
import { api } from '../api';
import { useBarcodeScanner } from '../hooks/useBarcodeScanner';
import { cameraScanSupported, useCameraScanner } from '../hooks/useCameraScanner';
import { useVoiceRecorder } from '../hooks/useVoiceRecorder';
import { PATHS } from '../navigation';
import { kicker, t } from '../theme';
import { useBatch, useStore } from '../store';

const BAR_HEIGHTS = [12, 26, 18, 30, 22, 14, 28, 20, 10];

const STAGE_TITLE: Record<string, string> = {
  waiting: 'Barcode scannen',
  'looking-up': 'Produkt wird gesucht …',
  known: 'Ablaufdatum erfassen',
  unknown: 'Barcode unbekannt',
  offline: 'Suche fehlgeschlagen',
};

export function Scan() {
  const navigate = useNavigate();
  const stage = useStore((s) => s.scanStage);
  const barcode = useStore((s) => s.scanBarcode);
  const message = useStore((s) => s.scanMessage);
  const draft = useStore((s) => s.draft);
  const sttReady = useStore((s) => s.sttAvailable);
  const voiceEnabled = useStore((s) => s.settings.voiceCapture);
  // Both must hold: a configured service AND the household wanting it on.
  const sttAvailable = sttReady && voiceEnabled;
  const promptOpen = useStore((s) => s.prompt !== null);
  const batch = useBatch();

  const { onBarcode, nameUnknown, openPrompt, patchDraft, endBatch, flash } = useStore.getState();

  const camera = useCameraScanner((code) => void onBarcode(code));
  const recorder = useVoiceRecorder();
  const [transcribing, setTranscribing] = useState(false);
  const [pairing, setPairing] = useState(false);

  // The HID scanner stays armed except while a keyboard overlay owns input.
  useBarcodeScanner({ enabled: !promptOpen, onScan: (code) => void onBarcode(code) });

  const today = todayIn(Intl.DateTimeFormat().resolvedOptions().timeZone);

  const scannerLabel =
    stage === 'looking-up' ? 'wird gesucht' : stage === 'known' ? 'erkannt' : stage === 'unknown' ? 'unbekannt' : 'Scanner bereit';
  const scannerColour = stage === 'known' ? t.accent : stage === 'unknown' || stage === 'offline' ? t.danger : 'oklch(0.7 0.05 90)';

  const enterBarcodeManually = () =>
    openPrompt({
      label: 'BARCODE EINTIPPEN',
      value: '',
      mode: 'numeric',
      onCommit: (value) => {
        useStore.setState({ prompt: null });
        if (value.length >= 6) void onBarcode(value);
        else flash('Barcode zu kurz');
      },
    });

  const handleMic = async () => {
    if (recorder.state === 'recording') {
      const wav = await recorder.stop();
      if (!wav) return;
      setTranscribing(true);
      try {
        const result = await api.transcribe(wav);
        if (result.date) {
          patchDraft({ bestBefore: result.date, transcript: result.text });
          flash(`Verstanden: ${formatDe(result.date)}`);
        } else {
          // Try locally too — the server parses, but a stale bundle might not.
          const local = parseGermanDate(result.text ?? '', today);
          if (local) {
            patchDraft({ bestBefore: local.date, transcript: result.text });
          } else {
            patchDraft({ transcript: result.text || null });
            flash('Kein Datum verstanden');
          }
        }
      } catch {
        flash('Spracherkennung nicht erreichbar');
      } finally {
        setTranscribing(false);
      }
      return;
    }
    await recorder.start();
  };

  const recording = recorder.state === 'recording';

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: '34px 38px 26px' }}>
      {/* header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20 }}>
        <div style={{ flex: 'none' }}>
          <div style={kicker}>{batch.length ? `EINRÄUMEN · ARTIKEL ${batch.length + 1}` : 'EINRÄUMEN'}</div>
          <div style={{ fontSize: 44, fontWeight: 500, letterSpacing: '-0.045em', lineHeight: 1.15, marginTop: 6, whiteSpace: 'nowrap' }}>
            {STAGE_TITLE[stage]}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            className="press"
            onClick={() => setPairing(true)}
            style={{ height: 58, padding: '0 22px', borderRadius: 16, border: t.hairlineStrong, display: 'flex', alignItems: 'center', fontSize: 19, fontWeight: 500, color: 'oklch(0.82 0.015 70)', whiteSpace: 'nowrap' }}
          >
            Handy als Scanner
          </div>
          <div
            className="press"
            onClick={enterBarcodeManually}
            style={{ height: 58, padding: '0 22px', borderRadius: 16, border: t.hairlineStrong, display: 'flex', alignItems: 'center', fontSize: 19, fontWeight: 500, color: 'oklch(0.82 0.015 70)' }}
          >
            Eintippen
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 22px', borderRadius: 999, background: t.surface, border: t.hairline, fontSize: 19, fontWeight: 500 }}>
            <div style={{ width: 10, height: 10, borderRadius: 999, background: scannerColour }} />
            {scannerLabel}
          </div>
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: '1.15fr 1fr', gap: 20, marginTop: 22 }}>
        {/* viewfinder */}
        <div style={{ borderRadius: 26, background: 'oklch(0.13 0.01 60)', border: t.hairline, position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(120% 90% at 50% 0%, oklch(0.26 0.012 60) 0%, oklch(0.13 0.01 60) 70%)' }} />

          <video
            ref={camera.videoRef}
            muted
            playsInline
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: camera.state === 'live' ? 1 : 0,
            }}
          />

          <div style={{ position: 'absolute', top: 24, left: 26, display: 'flex', alignItems: 'center', gap: 10, fontFamily: t.mono, fontSize: 13, letterSpacing: '0.14em', color: 'oklch(0.68 0.015 70)' }}>
            <div style={{ width: 9, height: 9, borderRadius: '50%', background: camera.state === 'live' ? t.danger : 'oklch(0.45 0.012 60)' }} />
            {camera.state === 'live'
              ? `LIVE${camera.resolution ? ` · ${camera.resolution}` : ''}${camera.frames ? ` · ${camera.frames} BILDER GEPRÜFT` : ''}`
              : 'HANDSCANNER'}
          </div>

          {/* framing brackets */}
          <div style={{ position: 'relative', width: 400, height: 198 }}>
            <div style={{ position: 'absolute', inset: 0, borderRadius: 18, border: '1px solid oklch(1 0 0 / 0.14)' }} />
            {(
              [
                ['Top', 'Left', '18px 0 0 0'],
                ['Top', 'Right', '0 18px 0 0'],
                ['Bottom', 'Left', '0 0 0 18px'],
                ['Bottom', 'Right', '0 0 18px 0'],
              ] as const
            ).map(([v, h, radius]) => (
              <div
                key={`${v}${h}`}
                style={{
                  position: 'absolute',
                  width: 52,
                  height: 52,
                  borderRadius: radius,
                  [`border${v}`]: `4px solid ${t.accent}`,
                  [`border${h}`]: `4px solid ${t.accent}`,
                  top: v === 'Top' ? -1 : 'auto',
                  bottom: v === 'Bottom' ? -1 : 'auto',
                  left: h === 'Left' ? -1 : 'auto',
                  right: h === 'Right' ? -1 : 'auto',
                }}
              />
            ))}
            {stage === 'waiting' && (
              <div style={{ position: 'absolute', left: 10, right: 10, height: 2, background: t.accent, borderRadius: 2, animation: 'sweep 2.4s ease-in-out infinite', top: '50%' }} />
            )}
          </div>

          {/* barcode readout */}
          <div style={{ position: 'absolute', bottom: 24, left: 24, right: 24, display: 'flex', alignItems: 'center', gap: 16, padding: '18px 22px', borderRadius: 18, background: 'oklch(0.22 0.012 60 / 0.85)', backdropFilter: 'blur(14px)', border: t.hairline }}>
            <div style={{ fontFamily: t.mono, fontSize: 20, letterSpacing: '0.06em', color: 'oklch(0.9 0.01 80)' }}>
              {barcode ? barcode.replace(/(\d{1,3})(?=(\d{3})+$)/g, '$1 ') : '— — — — — — —'}
            </div>
            <div style={{ flex: 1 }} />
            {!cameraScanSupported() ? (
              <div style={{ fontSize: 16, color: t.inkFaint, maxWidth: 260, textAlign: 'right' }}>
                Kamera-Scan wird von diesem Browser nicht unterstützt
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {/* Zoom before anything else: a barcode too small in frame has
                    too few pixels per bar, and no decoder setting recovers
                    that. Only rendered where the device offers it. */}
                {camera.state === 'live' && camera.zoomRange && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div
                      className="press"
                      onClick={() =>
                        void camera.setZoom(Math.max(camera.zoomRange!.min, camera.zoom - camera.zoomRange!.step * 4))
                      }
                      style={camControl}
                    >
                      −
                    </div>
                    <div style={{ fontFamily: t.mono, fontSize: 16, minWidth: 52, textAlign: 'center', color: t.inkMuted }}>
                      {camera.zoom.toFixed(1)}×
                    </div>
                    <div
                      className="press"
                      onClick={() =>
                        void camera.setZoom(Math.min(camera.zoomRange!.max, camera.zoom + camera.zoomRange!.step * 4))
                      }
                      style={camControl}
                    >
                      +
                    </div>
                  </div>
                )}
                {camera.state === 'live' && camera.torchAvailable && (
                  <div
                    className="press"
                    onClick={() => void camera.toggleTorch()}
                    style={{
                      ...camControl,
                      width: 'auto',
                      padding: '0 18px',
                      background: camera.torch ? t.accent : 'oklch(0.3 0.012 60)',
                      color: camera.torch ? t.accentInk : t.ink,
                    }}
                  >
                    Licht
                  </div>
                )}
                {/* Only worth showing when the device actually has a choice. */}
                {camera.state === 'live' && camera.cameras.length > 1 && (
                  <div
                    className="press"
                    onClick={() => void camera.switchCamera()}
                    style={{ height: 52, padding: '0 18px', borderRadius: 14, background: 'oklch(0.3 0.012 60)', display: 'flex', alignItems: 'center', gap: 8, fontSize: 18, fontWeight: 500, whiteSpace: 'nowrap' }}
                  >
                    {camera.cameras.find((c) => c.deviceId === camera.activeCamera)?.label ?? 'Kamera'}
                    <span style={{ color: t.accent }}>wechseln</span>
                  </div>
                )}
                <div
                  className="press"
                  onClick={() => (camera.state === 'live' ? camera.stop() : void camera.start())}
                  style={{ height: 52, padding: '0 20px', borderRadius: 14, background: 'oklch(0.3 0.012 60)', display: 'flex', alignItems: 'center', fontSize: 18, fontWeight: 500 }}
                >
                  {camera.state === 'live' ? 'Kamera aus' : 'Kamera an'}
                </div>
              </div>
            )}
          </div>

          {(camera.state === 'error' || camera.state === 'starting') && (
            <div
              style={{
                position: 'absolute',
                left: 24,
                right: 24,
                bottom: 104,
                padding: '16px 20px',
                borderRadius: 18,
                background: camera.state === 'error' ? 'oklch(0.26 0.05 30 / 0.94)' : 'oklch(0.24 0.012 60 / 0.94)',
                border: camera.state === 'error' ? '1px solid oklch(0.72 0.17 25 / 0.5)' : t.hairline,
                backdropFilter: 'blur(14px)',
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
            >
              <div style={{ fontFamily: t.mono, fontSize: 12, letterSpacing: '0.14em', color: camera.state === 'error' ? 'oklch(0.8 0.14 35)' : t.inkFaint }}>
                {camera.state === 'error' ? 'KAMERA' : 'KAMERA STARTET'}
              </div>
              <div style={{ fontSize: 19, fontWeight: 500, lineHeight: 1.3, textWrap: 'pretty' }}>
                {camera.state === 'error' ? camera.error : 'Einen Moment …'}
              </div>
              {camera.state === 'error' && (
                <div style={{ fontSize: 16, color: t.inkMuted, marginTop: 2 }}>
                  Der Handscanner funktioniert unabhängig davon weiter.
                </div>
              )}
            </div>
          )}
        </div>

        {/* right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minHeight: 0 }}>
          <div style={{ borderRadius: 26, background: t.surface, border: t.hairline, padding: 20, display: 'flex', gap: 18, alignItems: 'center', flex: 'none', opacity: draft ? 1 : 0.45 }}>
            <div
              style={{
                width: 84,
                height: 84,
                flex: 'none',
                borderRadius: 18,
                background: draft?.imageUrl
                  ? `center / cover no-repeat url(${draft.imageUrl})`
                  : 'repeating-linear-gradient(135deg, oklch(0.3 0.012 60) 0 8px, oklch(0.26 0.012 60) 8px 16px)',
              }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
              <div style={{ fontFamily: t.mono, fontSize: 12, letterSpacing: '0.14em', color: t.inkFaint }}>
                {draft ? 'AUS PRODUKTDATENBANK' : 'NOCH KEIN PRODUKT'}
              </div>
              <div style={{ fontSize: 29, fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.15, textWrap: 'pretty' }}>
                {draft?.name ?? 'Warte auf Scan'}
              </div>
              <div style={{ fontSize: 18, color: t.inkMuted }}>
                {draft?.bestBefore ? `Haltbar bis ${formatDe(draft.bestBefore)}` : 'Kein Datum erfasst'}
              </div>
            </div>
          </div>

          {stage === 'unknown' && (
            <div style={{ flex: 1, minHeight: 0, borderRadius: 26, background: 'oklch(0.24 0.03 60)', border: '1px solid oklch(0.72 0.17 25 / 0.45)', padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ fontFamily: t.mono, fontSize: 12, letterSpacing: '0.14em', color: 'oklch(0.78 0.14 40)' }}>NICHT IN DER DATENBANK</div>
              <div style={{ fontSize: 27, fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.25, textWrap: 'pretty' }}>
                Diesen Artikel einmal benennen — danach wird der Barcode wiedererkannt.
              </div>
              <div style={{ flex: 1 }} />
              <div className="press" onClick={nameUnknown} style={{ height: 74, borderRadius: 18, background: t.accent, color: t.accentInk, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 600 }}>
                Manuell anlegen
              </div>
              <div className="press" onClick={() => navigate(PATHS.inhalt)} style={{ height: 62, borderRadius: 18, border: t.hairlineStrong, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 500, color: 'oklch(0.8 0.015 70)' }}>
                Ohne Barcode erfassen
              </div>
            </div>
          )}

          {stage === 'offline' && (
            <div style={{ flex: 1, borderRadius: 26, background: 'oklch(0.24 0.03 60)', border: '1px solid oklch(0.72 0.17 25 / 0.45)', padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ fontFamily: t.mono, fontSize: 12, letterSpacing: '0.14em', color: 'oklch(0.78 0.14 40)' }}>VERBINDUNG</div>
              <div style={{ fontSize: 25, fontWeight: 600, lineHeight: 1.25, textWrap: 'pretty' }}>{message}</div>
              <div style={{ flex: 1 }} />
              <div className="press" onClick={() => barcode && void onBarcode(barcode)} style={{ height: 74, borderRadius: 18, background: t.accent, color: t.accentInk, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 600 }}>
                Erneut versuchen
              </div>
              <div className="press" onClick={nameUnknown} style={{ height: 62, borderRadius: 18, border: t.hairlineStrong, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 500, color: 'oklch(0.8 0.015 70)' }}>
                Trotzdem benennen
              </div>
            </div>
          )}

          {stage === 'known' && draft && (
            <div style={{ flex: 1, minHeight: 0, borderRadius: 26, background: t.surface, border: t.hairline, padding: 22, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
              <div style={{ fontFamily: t.mono, fontSize: 12, letterSpacing: '0.14em', color: t.inkFaint, alignSelf: 'flex-start' }}>
                {sttAvailable ? 'ABLAUFDATUM SPRECHEN' : !voiceEnabled ? 'SPRACH-ERFASSUNG DEAKTIVIERT' : 'SPRACHERKENNUNG AUS'}
              </div>

              <div
                className="press"
                onClick={() => sttAvailable && void handleMic()}
                style={{
                  width: 92,
                  height: 92,
                  borderRadius: 999,
                  background: recording ? t.danger : sttAvailable ? t.accent : 'oklch(0.3 0.012 60)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  animation: recording ? 'halo 1.6s ease-out infinite' : undefined,
                  cursor: sttAvailable ? 'pointer' : 'not-allowed',
                }}
              >
                <div style={{ width: 24, height: 38, borderRadius: 12, background: sttAvailable ? 'oklch(0.18 0.03 130)' : 'oklch(0.5 0.012 60)' }} />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 5, height: 28 }}>
                {BAR_HEIGHTS.map((height, index) => (
                  <div
                    key={index}
                    style={{
                      width: 6,
                      borderRadius: 3,
                      height: recording ? Math.max(6, height * (0.4 + recorder.level)) : 6,
                      background: recording ? `oklch(0.82 0.16 130 / ${0.5 + height / 60})` : 'oklch(0.45 0.012 60)',
                      transition: 'height 120ms',
                    }}
                  />
                ))}
              </div>

              <div style={{ width: '100%', flex: 1, minHeight: 0, borderRadius: 18, background: 'oklch(0.19 0.012 60)', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ fontFamily: t.mono, fontSize: 12, letterSpacing: '0.14em', color: 'oklch(0.6 0.015 70)' }}>
                  {transcribing ? 'WIRD ERKANNT' : recording ? 'ICH HÖRE' : 'ERFASST'}
                </div>
                <div style={{ fontSize: 24, fontWeight: 500, letterSpacing: '-0.01em', lineHeight: 1.3, textWrap: 'pretty' }}>
                  {recorder.error ??
                    (transcribing
                      ? '…'
                      : draft.transcript ??
                        (sttAvailable ? 'Tippe aufs Mikrofon und sag das Datum' : 'Datum auf dem nächsten Schritt eintippen'))}
                </div>
              </div>

              <div
                className="press"
                onClick={() => navigate(PATHS.confirm)}
                style={{ width: '100%', height: 74, borderRadius: 18, background: t.accent, color: t.accentInk, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 23, fontWeight: 600 }}
              >
                Weiter zur Prüfung
              </div>
            </div>
          )}

          {(stage === 'waiting' || stage === 'looking-up') && (
            <div style={{ flex: 1, borderRadius: 26, background: t.surface, border: t.hairline, padding: 24, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 12 }}>
              <div style={{ fontSize: 24, fontWeight: 500, color: t.inkMuted, lineHeight: 1.35, textWrap: 'pretty' }}>
                {stage === 'looking-up'
                  ? 'Suche in der Produktdatenbank …'
                  : 'Barcode vor den Handscanner halten — oder oben rechts eintippen.'}
              </div>
            </div>
          )}
        </div>
      </div>

      {pairing && <PhonePairing onClose={() => setPairing(false)} />}

      {/* "diese Runde" */}
      {batch.length > 0 && (
        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', borderRadius: 20, background: t.surface, border: t.hairline }}>
          <div style={{ fontFamily: t.mono, fontSize: 12, letterSpacing: '0.14em', color: t.inkFaint, flex: 'none' }}>DIESE RUNDE</div>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', gap: 8, overflowX: 'auto' }}>
            {batch.map((item) => (
              <div key={item.id} style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 9, padding: '10px 16px', borderRadius: 999, background: t.control, fontSize: 18, fontWeight: 500, whiteSpace: 'nowrap' }}>
                <div style={{ width: 8, height: 8, borderRadius: 999, background: t.accent }} />
                {item.name}
              </div>
            ))}
          </div>
          <div
            className="press"
            onClick={() => {
              endBatch();
              navigate(PATHS.inhalt);
              flash(`${batch.length} Artikel eingeräumt`);
            }}
            style={{ flex: 'none', height: 62, padding: '0 26px', borderRadius: 16, background: 'oklch(0.3 0.012 60)', display: 'flex', alignItems: 'center', fontSize: 20, fontWeight: 600 }}
          >
            Fertig
          </div>
        </div>
      )}
    </div>
  );
}

const camControl: React.CSSProperties = {
  height: 52,
  width: 52,
  borderRadius: 14,
  background: 'oklch(0.3 0.012 60)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 22,
  fontWeight: 500,
};
